import {EnabledPlugin, EnabledPlugins} from "./PluginHandler";

export interface PluginConfigItemInterface {
    title: string
    type: 'check' | 'select' | 'number' | 'string' | 'textarea' | 'color' | 'image' | 'group'
    help?: string
    defaultValue?: any
    min?: number
    max?: number
    rows?: number
    items?:  { [key: string]: PluginConfigItem }
    options?: string[]
}

type PluginConfigItemSelect = PluginConfigItemInterface & {
    options: string[]
}

type PluginConfigItemNumber = PluginConfigItemInterface & {}
type PluginConfigItemString = PluginConfigItemInterface & {}

type PluginConfigItem = PluginConfigItemSelect
                        | PluginConfigItemNumber
                        | PluginConfigItemString

export type DefaultPluginConfig = {
    enabled: boolean
}

export type PluginConfig<T extends DefaultPluginConfig> = T
export type PluginConfigPosition = {
    horizontal: 'left'|'right'
    horizontalSpace: number
    vertical: 'top'|'bottom'
    verticalSpace: number
}

type PluginSettings = { [key: string]: PluginConfigItem }

export type TestAction = {
    title: string
    description?: string
    buttons?: {
      [key: string]: string
    }[]
    config?: {
        [key: string]: PluginConfigItem
    }
    run: (data: PluginConfig<any>, config: any) => void
}

type DebugAction = {
    title: string
    run: (enabled: boolean) => void
}

type PluginInit<T extends DefaultPluginConfig> = {
    defaultSettings?: string[]
    supports?: 'position'[]
    settings: PluginSettings,
    initialize: (id: string, config: PluginConfig<T>, parentNode?: HTMLElement|null) => void
    onWsAction: (id: string, action: string, data: any, config: PluginConfig<any>) => boolean
    onConfig: (id: string, config: PluginConfig<T>) => void
    onRemove?: (id: string) => void
    testAction?: { [key: string] : TestAction }
    debugAction?: { [key: string] : TestAction }
}

type Plugin = {
    name: string,
    title: string,
    config: PluginConfig<any>
} & PluginInit<any>

export type PluginInstance = {
    id: string
} & Plugin

const plugins: Plugin[] = []

let allConfig = {}

const supportsConfig: { [key: string]: PluginSettings } = {
    position: {
        groupPosition: {
            type: 'group',
            title: 'Position',
            items: {
                horizontal: {
                    title: 'Horizontal',
                    type: 'select',
                    options: ['left', 'right'],
                    defaultValue: 'left',
                },
                horizontalSpace: {
                    title: 'Space',
                    type: 'number',
                    defaultValue: 30,
                },
                vertical: {
                    title: 'Vertical',
                    type: 'select',
                    options: ['top', 'bottom'],
                    defaultValue: 'bottom',
                },
                verticalSpace: {
                    title: 'Space',
                    type: 'number',
                    defaultValue: 153,
                },
            }
        },
    }
}

const registerPlugin = <T extends DefaultPluginConfig>(
    name: string,
    title: string,
    init: PluginInit<T>,
) => {
    init.settings = {
        enabled: {
            title: 'enabled',
            type: 'check',
            defaultValue: true,
        },
        ...init.settings,
    }

    if (typeof init.supports !== 'undefined') {
        for (const key of init.supports) {
            init.settings = {
                ...init.settings,
                ...supportsConfig[key],
            }
        }
    }

    plugins.push({
        name,
        title,
        ...init,
        config: Object.keys(init.settings).reduce((res, key) => {
            res[key] = init.settings.defaultValue
            return res
        }, {}),
        testAction: init.testAction,
        debugAction: init.debugAction,
    })
}

const initPlugins = (enabledPlugins: EnabledPlugins) => {
    const instances = []

    for (const enabledPlugin of Object.values(enabledPlugins)) {
        instances.push(initPlugin(enabledPlugin))
    }

    return instances
}

export const initPlugin = <T extends DefaultPluginConfig>(enabledPlugin: EnabledPlugin<T>, parentNode: HTMLElement|null = null) => {
    if (!parentNode) {
        parentNode = document.body
    }
    const registeredPlugin = plugins.find(p => p.name === enabledPlugin.plugin)
    if (!registeredPlugin) {
        console.error('failed to initialize missing plugin', enabledPlugin.plugin, plugins)
        return
    }

    const instance: PluginInstance = {
        id: enabledPlugin.id,
        ...registeredPlugin,
        config: enabledPlugin.config,
    }
    console.log('trigger init', enabledPlugin.plugin, enabledPlugin.id, parentNode)
    instance.initialize(enabledPlugin.id, enabledPlugin.config, parentNode)

    return instance
}

const getPluginConfig = (name: string): PluginConfig<any> => {
    const plugin = plugins.find(p => p.name === name)
    if (!plugin) {
        return {}
    }

    let defaultConfig = {}
    if (typeof plugin.supports !== 'undefined') {
        for (const key of plugin.supports) {
            defaultConfig = {
                ...defaultConfig,
                ...supportsConfig[key],
            }
        }
    }

    const overrides = typeof allConfig[plugin.name] !== 'undefined' ? allConfig[plugin.name] : {}

    return Object.keys({ ...defaultConfig, ...plugin.settings }).reduce((res, key) => {
        res[key] = typeof overrides[key] !== 'undefined' ? overrides[key] : getPluginConfigDefaultValue(plugin.name, key)
        return res
    }, {})
}

export const getDefaultPluginConfig = (name: string, overrides: any = {}): PluginConfig<any> => {
    const plugin = plugins.find(p => p.name === name)

    if (!plugin) {
        return {}
    }

    let defaultConfig = {}
    if (typeof plugin.supports !== 'undefined') {
        for (const key of plugin.supports) {
            defaultConfig = {
                ...defaultConfig,
                ...supportsConfig[key],
            }
        }
    }

    let res = defaultConfig
    for (const key of Object.keys(plugin.settings)) {
        if (plugin.settings[key].type !== 'group') {
            res[key] = typeof overrides[key] !== 'undefined' ? overrides[key] : plugin.settings[key].defaultValue
        } else {
            for (const key2 of Object.keys(plugin.settings[key].items)) {
                res[key2] = typeof overrides[key2] !== 'undefined' ? overrides[key2] : plugin.settings[key].items[key2].defaultValue
            }
        }
    }

    return res
}

const getPluginConfigDefaultValue = (name: string, key: string): any => {
    const plugin = plugins.find(p => p.name === name)
    if (!plugin) {
        return null
    }

    if (plugin.settings[key].type === 'group') {
        return null
    }

    let res = typeof plugin.settings[key] !== 'undefined' ? plugin.settings[key].defaultValue : null

    if (!res) {
        console.log('searching default value for', key, 'in', plugin.settings)
        for (const key2 of Object.keys(plugin.settings)) {
            if (plugin.settings[key2].type === 'group') {
                if (typeof plugin.settings[key2].items[key] !== 'undefined') {
                    return plugin.settings[key2].items[key].defaultValue
                } else {
                    console.log('not found in', plugin.settings[key2].items)
                }
            }
        }
    }
    return res
}

const getPlugins = () => {
    return plugins
}

const getPluginsWIthConfigAndSettings = () => {
    return getPlugins().map(p => ({
        ...p,
        config: getPluginConfig(p.name),
    }))
}

export {
    registerPlugin,
    initPlugins,
    getPlugins,
    getPluginConfig,
    getPluginConfigDefaultValue,
    getPluginsWIthConfigAndSettings,
}